/**
 * Approve from email (2026-10-01). The email buttons open
 * /approve#t=<token>&d=<decision>. The token stays in the URL fragment
 * (never sent to a server or in a Referer); this page posts it to the API.
 * Nothing is decided until the person presses Confirm — mail scanners that
 * pre-open links can't approve anything.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react'

import { Button } from '../components/ui/button'
import { apiClient } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'

interface Preview {
  kind: 'gate' | 'intake'
  decisions: string[]
  approver: string
  title: string
  link: string
  project?: string
  checklist?: Array<{ kind: string; name: string; status: string }>
  description?: string | null
  business_case?: string | null
  sponsor?: string | null
  score?: number | null
  estimated_cost?: number | null
  estimated_weeks?: number | null
}

const LABEL: Record<string, string> = { approved: 'Approve', sent_back: 'Send back', rejected: 'Reject', deferred: 'Defer' }
const NEEDS_NOTE = new Set(['sent_back'])

function readFragment(): { token: string; decision: string } {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return { token: params.get('t') ?? '', decision: params.get('d') ?? '' }
}

export default function ApprovePage() {
  const initial = useMemo(readFragment, [])
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(initial.token ? null : 'This link is incomplete. Open the item in Karyva instead.')
  const [decision, setDecision] = useState(initial.decision)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ message: string; link: string } | null>(null)

  useEffect(() => {
    if (!initial.token) return
    // Keep the token out of the visible address bar / screenshots.
    window.history.replaceState(null, '', window.location.pathname)
    apiClient.post<Preview>('/api/v1/approvals/link/preview', { token: initial.token })
      .then(({ data }) => {
        setPreview(data)
        if (!data.decisions.includes(initial.decision)) setDecision(data.decisions[0])
      })
      .catch((err) => setError(apiErrorMessage(err, 'This approval link has expired or was already used.')))
  }, [initial])

  const confirm = async () => {
    setBusy(true)
    try {
      const { data } = await apiClient.post('/api/v1/approvals/link/decide', {
        token: initial.token, decision, note: note.trim() || null,
      })
      setDone(data)
    } catch (err) {
      setError(apiErrorMessage(err, 'That didn’t work. Open the item in Karyva instead.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-screen items-start justify-center bg-background px-4 py-12">
      <div className="w-full max-w-xl space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-primary" /> Karyva approval</div>

        {done ? (
          <div className="space-y-3 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
            <h1 className="text-xl font-semibold text-foreground">{done.message}</h1>
            <p className="text-sm text-muted-foreground">It’s recorded in the audit log as decided from an email link.</p>
            <Link to={done.link} className="text-sm text-primary hover:underline">Open in Karyva</Link>
          </div>
        ) : error ? (
          <div className="space-y-3 text-center">
            <XCircle className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="text-foreground">{error}</p>
            <Link to="/login" className="text-sm text-primary hover:underline">Sign in to Karyva</Link>
          </div>
        ) : !preview ? (
          <p className="text-sm text-muted-foreground">Checking the link…</p>
        ) : (
          <>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{preview.title}</h1>
              {preview.project && <p className="text-sm text-muted-foreground">{preview.project}</p>}
            </div>

            {preview.kind === 'gate' && preview.checklist && (
              <ul className="space-y-1 text-sm">
                {preview.checklist.map((i) => (
                  <li key={`${i.kind}-${i.name}`} className="flex justify-between gap-2">
                    <span className="text-foreground">{i.name}</span>
                    <span className="text-muted-foreground">{i.status.replace('_', ' ')}</span>
                  </li>
                ))}
              </ul>
            )}
            {preview.kind === 'intake' && (
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  {[preview.sponsor && `Sponsor: ${preview.sponsor}`,
                    preview.estimated_cost != null && `Estimated cost: ${preview.estimated_cost.toLocaleString()}`,
                    preview.estimated_weeks && `${preview.estimated_weeks} weeks`,
                    preview.score != null && `Score ${Math.round(preview.score)}/100`].filter(Boolean).join(' · ')}
                </p>
                {(preview.business_case || preview.description) && <p className="text-foreground">{preview.business_case || preview.description}</p>}
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-foreground">Your decision</legend>
              <div className="flex flex-wrap gap-2">
                {preview.decisions.map((d) => (
                  <label key={d} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border px-3 text-sm ${decision === d ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'}`}>
                    <input type="radio" name="decision" value={d} checked={decision === d} onChange={() => setDecision(d)} className="sr-only" />
                    {LABEL[d] ?? d}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="block text-sm text-foreground">
              {NEEDS_NOTE.has(decision) ? 'What needs to change? (required)' : 'Note (optional)'}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000}
                className="mt-1 w-full rounded-md border border-border bg-background p-2 text-sm text-foreground" />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Deciding as {preview.approver} · this link works once</span>
              <Button onClick={confirm} disabled={busy || !decision || (NEEDS_NOTE.has(decision) && !note.trim())}>
                {busy ? 'Saving…' : `Confirm: ${LABEL[decision] ?? decision}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  )
}
